import { getNestedSubelementsByType, getMostRecentFieldValue, getPersonName } from '@fs/zion-sls'

/**
 * Extracts primary event information from a record
 * @param {Object} record - The record element
 * @returns {Object} Primary event details
 */
export const getRecordPrimaryEventData = (record) => {
  const recordEvents = getNestedSubelementsByType(record, 'EVENT') || []
  const primaryEvent = recordEvents.find((ev) => ev?.primary)

  const date = getNestedSubelementsByType(primaryEvent, 'DATE', true)?.[0]
  const place = getNestedSubelementsByType(primaryEvent, 'PLACE', true)?.[0]

  return {
    event: primaryEvent,
    date: getMostRecentFieldValue(date),
    place: getMostRecentFieldValue(place),
  }
}

/**
 * Formats record display data as a string
 * @param {Object} record - The record element
 * @returns {{primaryEvent: Object, primaryEventSummary: string}} Formatted record display data and event object
 */
export const formatRecordDisplayData = (record) => {
  const typeLabel = record?.displayLabel ?? record?.label
  // When creating new records, the default type is "RECORD" which is not informative to display, so we omit it
  const typeLabelToDisplay = typeLabel !== 'RECORD' ? typeLabel : null
  const { date, place, event } = getRecordPrimaryEventData(record)

  return { primaryEvent: event, primaryEventSummary: [typeLabelToDisplay, date, place].filter(Boolean).join(' | ') }
}

/**
 * Finds the primary person element in a household.
 *
 * If there are multiple primary persons, it returns the first one found.
 *
 * @param {Object} household - The household/record SLS object
 * @returns {Object|undefined} The primary person element, or undefined if there is none
 */
export const getPrimaryPerson = (household) => {
  const householdPersons = getNestedSubelementsByType(household, 'PERSON') || []

  return householdPersons.find((person) => person?.primary)
}

/**
 * Extracts the name of the primary person from a household.
 *
 * If there are multiple primary persons, it returns the name of the first one found.
 *
 * @param {Object} household - The household/record SLS object
 * @returns {string|null} The primary person's name, or null if no primary person exists
 */
export const getPrimaryPersonName = (household) => {
  const primaryPerson = getPrimaryPerson(household)

  return primaryPerson ? getPersonName({ person: primaryPerson, justOneName: true }) : null
}
